"use client";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Film } from "lucide-react";
import type { LibraryContext } from "@/native-shell/library";
import { UUID } from "@/lib/companion/outings";
import { COMPANION_SHARED_PLANS_CHANGED } from "@/lib/companion/events";
import {
  readFeedPage,
  type SharedClip,
} from "@/lib/companion/freestyle/community";
import { communityRequest } from "@/lib/companion/freestyle/community-client";
import { useFreestyleAccount } from "./FreestyleShared";

/** The page's expected owner and local account must agree; the API verifies access. */
export function FreestylePreview({ owner }: { owner: string }) {
  const { context, visible } = useFreestyleAccount();
  if (!context || !visible || context.owner !== owner || !UUID.test(owner))
    return null;
  return (
    <PreviewBody key={`${owner}:${context.generation}`} context={context} />
  );
}

function PreviewBody({ context }: { context: LibraryContext }) {
  const element = useRef<HTMLDivElement>(null);
  const [nearby, setNearby] = useState(false),
    [clips, setClips] = useState<SharedClip[]>([]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNearby(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    if (element.current) observer.observe(element.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!nearby) return;
    let stopped = false,
      run = 0,
      failures = 0;
    let poll: ReturnType<typeof setTimeout> | undefined,
      lease: ReturnType<typeof setTimeout> | undefined;
    let request: AbortController | null = null;
    const load = async () => {
      const current = ++run;
      if (poll) clearTimeout(poll);
      request?.abort();
      request = new AbortController();
      try {
        const page = readFeedPage(
          await communityRequest(
            context,
            { view: "preview" },
            undefined,
            request.signal,
          ),
          context.owner,
        );
        if (stopped || current !== run) return;
        if (page.clips.length > 3 || page.snapshot !== null)
          throw new Error("Preview could not be checked.");
        setClips(page.clips);
        failures = 0;
        if (lease) clearTimeout(lease);
        lease = setTimeout(() => setClips([]), 30_000);
      } catch {
        if (stopped || current !== run) return;
        setClips([]);
        failures++;
      } finally {
        if (!stopped && current === run)
          poll = setTimeout(
            () => void load(),
            Math.min(120_000, 15_000 * 2 ** failures),
          );
      }
    };
    const changed = () => {
      setClips([]);
      void load();
    };
    void load();
    window.addEventListener(COMPANION_SHARED_PLANS_CHANGED, changed);
    return () => {
      stopped = true;
      run++;
      request?.abort();
      if (poll) clearTimeout(poll);
      if (lease) clearTimeout(lease);
      window.removeEventListener(COMPANION_SHARED_PLANS_CHANGED, changed);
    };
  }, [context, nearby]);
  return (
    <div ref={element} className="ph-no-capture">
      {clips.length > 0 && (
        <section
          aria-label="Freestyle from your communities"
          className="mt-4 rounded-xl border border-border-light bg-white p-4 sm:p-5"
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-teal">
                <Film className="size-4" aria-hidden="true" />
                Freestyle
              </p>
              <h2 className="mt-2 font-heading text-xl font-bold text-navy">
                From your communities
              </h2>
            </div>
            <Link
              href="/companion/people/freestyle/feed"
              className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-teal"
            >
              Watch clips
              <ArrowUpRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <p className="mt-1 text-sm leading-6 text-text-muted">
            Small wins and favourite moments from your Squads and resorts.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {clips.map((clip) => (
              <Link
                key={`${clip.id}:${clip.generation}`}
                href={`/companion/people/freestyle/feed?clip=${clip.id}`}
                aria-label={`Open ${clip.trick} in Freestyle`}
                className="group flex min-w-0 items-center gap-3 rounded-lg border border-border-light p-2 hover:border-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:block"
              >
                <div className="relative aspect-video w-24 shrink-0 overflow-hidden rounded-md bg-navy sm:w-full">
                  <Image
                    src={clip.poster}
                    alt=""
                    fill
                    sizes="(max-width: 639px) 96px, 320px"
                    unoptimized
                    referrerPolicy="no-referrer"
                    className="object-cover"
                  />
                </div>
                <div className="min-w-0 sm:mt-3">
                  <p className="truncate font-heading text-sm font-bold text-navy">
                    {clip.trick}
                  </p>
                  <p className="truncate text-xs leading-5 text-text-muted">
                    {clip.authorName} · {clip.audience}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
